/**
 * Image Cropper Web Component
 *
 * A reusable web component for client-side image cropping with configurable
 * aspect ratios, drag-and-drop upload, and flexible integration.
 * @element image-cropper
 * @attr {string} aspect-ratio - Target aspect ratio (default: "1:1")
 * @attr {number} max-file-size - Max file size in bytes (default: 5242880 = 5MB)
 * @attr {string} min-dimensions - Min dimensions "WxH" (default: "64x64")
 * @attr {string} max-dimensions - Max dimensions "WxH" (default: "4096x4096")
 * @attr {string} output-format - Output MIME type (default: "image/png")
 * @attr {string} accept - Accepted file types (default: "image/jpeg,image/png,image/webp,image/svg+xml")
 * @attr {boolean} show-preview - Show preview after crop (default: true)
 * @attr {boolean} auto-crop - Auto-proceed to crop for non-matching aspect ratios (default: false)
 * @attr {string} mode - Usage mode: "avatar", "photo", etc. (default: "")
 * @fires image-loaded - Fired when image is loaded
 * @fires crop-complete - Fired when cropping is complete
 * @fires crop-change - Fired when crop area changes
 * @fires error - Fired on validation or processing errors
 */
export class ImageCropper extends HTMLElement {
  /**
   *
   */
  static get observedAttributes() {
    return [
      'aspect-ratio',
      'max-file-size',
      'min-dimensions',
      'max-dimensions',
      'output-format',
      'accept',
      'show-preview',
      'auto-crop',
      'mode',
    ];
  }

  /**
   *
   */
  constructor() {
    super();
    this.attachShadow({ mode: 'open' });

    // Internal state
    this._image = null;
    this._cropData = {
      x: 50,
      y: 50,
      size: 200,
    };
    this._scale = 1;
    this._originalBlob = null;
    this._croppedBlob = null;
    this._isSquare = false;
    this._isDragging = false;
    this._isResizing = false;
    this._currentHandle = null;
    this._startX = 0;
    this._startY = 0;

    // Config defaults
    this._config = {
      aspectRatio: '1:1',
      maxFileSize: 5242880, // 5MB
      minDimensions: { width: 64, height: 64 },
      maxDimensions: { width: 4096, height: 4096 },
      outputFormat: 'image/png',
      accept: ['image/jpeg', 'image/png', 'image/webp', 'image/svg+xml'],
      showPreview: true,
      autoCrop: false,
      mode: '',
    };
  }

  /**
   *
   */
  connectedCallback() {
    this._render();
    this._setupEventListeners();
  }

  /**
   *
   */
  disconnectedCallback() {
    this._cleanup();
  }

  /**
   *
   * @param name
   * @param oldValue
   * @param newValue
   */
  attributeChangedCallback(name, oldValue, newValue) {
    if (oldValue === newValue) return;

    switch (name) {
      case 'aspect-ratio':
        this._config.aspectRatio = newValue || '1:1';
        break;
      case 'max-file-size':
        this._config.maxFileSize = parseInt(newValue) || 5242880;
        break;
      case 'min-dimensions':
        this._parseDimensions(newValue, 'minDimensions');
        break;
      case 'max-dimensions':
        this._parseDimensions(newValue, 'maxDimensions');
        break;
      case 'output-format':
        this._config.outputFormat = newValue || 'image/png';
        break;
      case 'accept':
        this._config.accept = newValue ? newValue.split(',').map((t) => t.trim()) : [];
        break;
      case 'show-preview':
        this._config.showPreview = newValue !== 'false';
        break;
      case 'auto-crop':
        this._config.autoCrop = newValue === 'true';
        break;
      case 'mode':
        this._config.mode = newValue || '';
        break;
    }
  }

  /**
   * Public API: Get the cropped image as a Blob
   * @returns {Promise<Blob|null>}
   */
  async getCroppedBlob() {
    return this._croppedBlob;
  }

  /**
   * Public API: Get crop data
   * @returns {object} Crop data with x, y, width, height, aspectRatio
   */
  getCropData() {
    if (!this._image) return null;

    return {
      x: this._cropData.x / this._scale,
      y: this._cropData.y / this._scale,
      width: this._cropData.size / this._scale,
      height: this._cropData.size / this._scale,
      aspectRatio: this._config.aspectRatio,
      originalAspectRatio: this._image ? `${this._image.width}:${this._image.height}` : null,
      wasSquare: this._isSquare,
    };
  }

  /**
   * Public API: Load an image programmatically
   * @param {File} file - Image file to load
   */
  loadImage(file) {
    this._handleFileLoad(file);
  }

  /**
   * Public API: Reset to initial state
   */
  reset() {
    this._resetToInitialState();
  }

  /**
   * Public API: Get original image blob
   * @returns {Blob|null}
   */
  getOriginalBlob() {
    return this._originalBlob;
  }

  // ============================================================================
  // PRIVATE METHODS - Rendering
  // ============================================================================

  /**
   *
   */
  _render() {
    this.shadowRoot.innerHTML = `
      ${this._getStyles()}
      <div class="container">
        <div class="upload-area" id="uploadArea" part="upload-area">
          <label for="fileInput" class="upload-label" part="upload-label">
            Choose Image
          </label>
          <input type="file" id="fileInput" accept="${this._config.accept.join(',')}" />
          <p class="upload-hint" part="upload-hint">or drag and drop an image here</p>
        </div>

        <div class="aspect-ratio-notice" id="aspectRatioNotice">
          <h3>Image Analysis</h3>
          <div class="ratio-info" id="ratioInfo"></div>
          <div class="dimensions" id="dimensionsInfo"></div>
          <div id="squareActions" style="display: none;">
            <button class="btn btn-success" id="useAsIsBtn">
              ✓ Use As Is
            </button>
            <button class="btn btn-secondary" id="cropAnywayBtn">
              ✂ Crop Anyway
            </button>
          </div>
        </div>

        <div class="cropper-container" id="cropperContainer">
          <div class="canvas-wrapper">
            <canvas id="canvas"></canvas>
            <div class="crop-box" id="cropBox">
              <div class="crop-handle handle-nw" data-handle="nw"></div>
              <div class="crop-handle handle-ne" data-handle="ne"></div>
              <div class="crop-handle handle-sw" data-handle="sw"></div>
              <div class="crop-handle handle-se" data-handle="se"></div>
              <div class="crop-grid">
                <div class="grid-line horizontal"></div>
                <div class="grid-line horizontal"></div>
                <div class="grid-line vertical"></div>
                <div class="grid-line vertical"></div>
              </div>
            </div>
          </div>
        </div>

        <div class="controls" id="controls">
          <button class="btn btn-primary" id="cropBtn" part="button crop-button">
            ✂ Crop Image
          </button>
          <button class="btn btn-primary hidden" id="downloadBtn" style="display: none;" part="button download-button">
            ⬇ Download
          </button>
          <button class="btn btn-secondary" id="resetBtn" part="button reset-button">
            ↻ Reset
          </button>
          <button class="btn btn-secondary" id="newImageBtn" part="button new-image-button">
            + New Image
          </button>
        </div>

        <div class="preview-container" id="previewContainer">
          <h3>Cropped Preview</h3>
          <div class="preview-wrapper">
            <img id="preview" alt="Cropped preview" />
            <div class="info" id="previewInfo"></div>
          </div>
        </div>
      </div>
    `;
  }

  /**
   *
   */
  _getStyles() {
    return `
      <style>
        * {
          margin: 0;
          padding: 0;
          box-sizing: border-box;
        }

        :host {
          display: block;
          /* Namespaced tokens (--ic-*) — bridges.css overrides these on the
           * <image-cropper> element. Each falls back to the legacy generic
           * parent var (e.g. --primary-color) so guest-site styling that
           * already sets those continues to work, then to a literal default
           * so the component renders standalone. */
          --ic-primary-color: var(--primary-color, #667eea);
          --ic-secondary-color: var(--secondary-color, #764ba2);
          --ic-border-radius: var(--border-radius, 8px);
          --ic-crop-box-border-color: var(--crop-box-border-color, #667eea);
          --ic-crop-box-border-width: var(--crop-box-border-width, 2px);
          --ic-overlay-opacity: var(--overlay-opacity, 0.5);
          --ic-handle-size: var(--handle-size, 12px);
          --ic-handle-color: var(--handle-color, white);
          --ic-grid-color: var(--grid-color, rgba(255, 255, 255, 0.5));
          --ic-button-padding: var(--button-padding, 12px 25px);
          --ic-button-font-size: var(--button-font-size, 16px);
          --ic-button-font-weight: var(--button-font-weight, 500);
          --ic-upload-area-background: var(--upload-area-background, #f8f9ff);
          --ic-upload-area-border-color: var(--upload-area-border-color, #667eea);
          --ic-upload-label-background: var(--upload-label-background, linear-gradient(135deg, #667eea 0%, #764ba2 100%));
        }

        .container {
          font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Oxygen, Ubuntu, sans-serif;
          width: 100%;
        }

        .upload-area {
          border: 3px dashed var(--ic-upload-area-border-color);
          border-radius: 15px;
          padding: 40px;
          text-align: center;
          margin-bottom: 30px;
          transition: all 0.3s ease;
          cursor: pointer;
          background: var(--ic-upload-area-background);
        }

        .upload-area:hover {
          border-color: var(--ic-secondary-color);
          background: color-mix(in srgb, var(--ic-upload-area-background) 90%, black);
        }

        .upload-area.dragover {
          background: color-mix(in srgb, var(--ic-upload-area-background) 80%, black);
          border-color: var(--ic-secondary-color);
        }

        .upload-label {
          display: inline-block;
          padding: 12px 30px;
          background: var(--ic-upload-label-background);
          color: white;
          border-radius: var(--ic-border-radius);
          cursor: pointer;
          transition: transform 0.2s;
          font-weight: 500;
        }

        .upload-label:hover {
          transform: scale(1.05);
        }

        #fileInput {
          display: none;
        }

        .upload-hint {
          margin-top: 15px;
          color: #666;
          font-size: 14px;
        }

        .aspect-ratio-notice {
          display: none;
          background: linear-gradient(135deg, #f5f5f5, #e8e8e8);
          border-radius: 15px;
          padding: 30px;
          margin-bottom: 30px;
          text-align: center;
          box-shadow: 0 5px 15px rgba(0,0,0,0.1);
        }

        .aspect-ratio-notice h3 {
          color: #333;
          margin-bottom: 15px;
          font-size: 20px;
        }

        .ratio-info {
          display: inline-block;
          background: white;
          padding: 10px 20px;
          border-radius: var(--ic-border-radius);
          margin: 10px 0 20px;
          font-weight: 500;
          color: var(--ic-primary-color);
          box-shadow: 0 2px 5px rgba(0,0,0,0.1);
        }

        .dimensions {
          color: #666;
          font-size: 14px;
          margin-bottom: 20px;
        }

        .square-badge {
          display: inline-block;
          background: linear-gradient(135deg, #4caf50, #45a049);
          color: white;
          padding: 5px 15px;
          border-radius: 20px;
          font-size: 14px;
          font-weight: 500;
          margin-left: 10px;
        }

        .cropper-container {
          display: none;
          margin-bottom: 30px;
          text-align: center;
        }

        .canvas-wrapper {
          position: relative;
          display: inline-block;
          margin: 0 auto;
          background: #f0f0f0;
          border-radius: 10px;
          overflow: hidden;
          box-shadow: 0 10px 30px rgba(0,0,0,0.1);
        }

        #canvas {
          display: block;
          max-width: 100%;
          height: auto;
        }

        .crop-box {
          position: absolute;
          border: var(--ic-crop-box-border-width) solid var(--ic-crop-box-border-color);
          background: rgba(255, 255, 255, 0.3);
          cursor: move;
          box-shadow: 0 0 0 9999px rgba(0, 0, 0, var(--ic-overlay-opacity));
        }

        .crop-handle {
          position: absolute;
          width: var(--ic-handle-size);
          height: var(--ic-handle-size);
          background: var(--ic-handle-color);
          border: 2px solid var(--ic-crop-box-border-color);
          border-radius: 50%;
          transition: transform 0.2s;
        }

        .crop-handle:hover {
          background: var(--ic-crop-box-border-color);
          transform: scale(1.2);
        }

        .handle-nw { top: calc(var(--ic-handle-size) / -2); left: calc(var(--ic-handle-size) / -2); cursor: nw-resize; }
        .handle-ne { top: calc(var(--ic-handle-size) / -2); right: calc(var(--ic-handle-size) / -2); cursor: ne-resize; }
        .handle-sw { bottom: calc(var(--ic-handle-size) / -2); left: calc(var(--ic-handle-size) / -2); cursor: sw-resize; }
        .handle-se { bottom: calc(var(--ic-handle-size) / -2); right: calc(var(--ic-handle-size) / -2); cursor: se-resize; }

        .crop-grid {
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          pointer-events: none;
        }

        .grid-line {
          position: absolute;
          background: var(--ic-grid-color);
        }

        .grid-line.horizontal {
          width: 100%;
          height: 1px;
        }

        .grid-line.vertical {
          height: 100%;
          width: 1px;
        }

        .grid-line:nth-child(1) { top: 33.33%; }
        .grid-line:nth-child(2) { top: 66.66%; }
        .grid-line:nth-child(3) { left: 33.33%; }
        .grid-line:nth-child(4) { left: 66.66%; }

        .controls {
          display: none;
          gap: 15px;
          justify-content: center;
          flex-wrap: wrap;
          margin-bottom: 20px;
        }

        .btn {
          padding: var(--ic-button-padding);
          border: none;
          border-radius: var(--ic-border-radius);
          font-size: var(--ic-button-font-size);
          font-weight: var(--ic-button-font-weight);
          cursor: pointer;
          transition: all 0.3s ease;
          display: inline-flex;
          align-items: center;
          gap: 8px;
          font-family: inherit;
        }

        .btn-primary {
          background: linear-gradient(135deg, var(--ic-primary-color) 0%, var(--ic-secondary-color) 100%);
          color: white;
        }

        .btn-primary:hover {
          transform: translateY(-2px);
          box-shadow: 0 5px 15px rgba(102, 126, 234, 0.4);
        }

        .btn-primary:active {
          transform: translateY(0);
        }

        .btn:disabled {
          opacity: 0.6;
          cursor: not-allowed;
          transform: none !important;
        }

        .btn.hidden {
          display: none !important;
          visibility: hidden !important;
        }

        .btn-secondary {
          background: #e0e0e0;
          color: #333;
        }

        .btn-secondary:hover {
          background: #d0d0d0;
        }

        .btn-success {
          background: linear-gradient(135deg, #4caf50, #45a049);
          color: white;
        }

        .btn-success:hover {
          transform: translateY(-2px);
          box-shadow: 0 5px 15px rgba(76, 175, 80, 0.4);
        }

        .preview-container {
          display: none;
          text-align: center;
          margin-top: 30px;
        }

        .preview-container h3 {
          margin-bottom: 15px;
          color: #333;
        }

        .preview-wrapper {
          display: inline-block;
          background: #f0f0f0;
          padding: 20px;
          border-radius: 10px;
          box-shadow: 0 5px 20px rgba(0,0,0,0.1);
        }

        #preview {
          max-width: 300px;
          max-height: 300px;
          border-radius: var(--ic-border-radius);
          box-shadow: 0 3px 10px rgba(0,0,0,0.2);
        }

        .info {
          margin-top: 15px;
          color: #666;
          font-size: 14px;
        }

        @media (max-width: 600px) {
          .container {
            padding: 10px;
          }

          .controls {
            flex-direction: column;
          }

          .btn {
            width: 100%;
            justify-content: center;
          }
        }
      </style>
    `;
  }

  /**
   *
   * @param name
   * @param defaultValue
   */
  _getCSSVar(name, defaultValue) {
    const style = getComputedStyle(this);
    const value = style.getPropertyValue(name).trim();
    return value || defaultValue;
  }

  // ============================================================================
  // PRIVATE METHODS - Event Listeners
  // ============================================================================

  /**
   *
   */
  _setupEventListeners() {
    const fileInput = this.shadowRoot.getElementById('fileInput');
    const uploadArea = this.shadowRoot.getElementById('uploadArea');
    const cropBtn = this.shadowRoot.getElementById('cropBtn');
    const downloadBtn = this.shadowRoot.getElementById('downloadBtn');
    const resetBtn = this.shadowRoot.getElementById('resetBtn');
    const newImageBtn = this.shadowRoot.getElementById('newImageBtn');
    const useAsIsBtn = this.shadowRoot.getElementById('useAsIsBtn');
    const cropAnywayBtn = this.shadowRoot.getElementById('cropAnywayBtn');
    const cropBox = this.shadowRoot.getElementById('cropBox');
    const handles = this.shadowRoot.querySelectorAll('.crop-handle');

    // File input
    fileInput.addEventListener('change', (e) => this._handleFileSelect(e));

    // Drag and drop
    uploadArea.addEventListener('dragover', (e) => {
      e.preventDefault();
      uploadArea.classList.add('dragover');
    });

    uploadArea.addEventListener('dragleave', () => {
      uploadArea.classList.remove('dragover');
    });

    uploadArea.addEventListener('drop', (e) => {
      e.preventDefault();
      uploadArea.classList.remove('dragover');
      const files = e.dataTransfer.files;
      if (files.length > 0 && this._isValidFileType(files[0])) {
        this._handleFileLoad(files[0]);
      }
    });

    // Crop box interactions
    cropBox.addEventListener('mousedown', (e) => this._startDrag(e));
    cropBox.addEventListener('touchstart', (e) => this._startDrag(e), { passive: false });

    // Handle resizing
    handles.forEach((handle) => {
      handle.addEventListener('mousedown', (e) => this._startResize(e));
      handle.addEventListener('touchstart', (e) => this._startResize(e), { passive: false });
    });

    // Document events for drag/resize
    this._boundHandleMove = (e) => this._handleMove(e);
    this._boundStopInteraction = () => this._stopInteraction();

    document.addEventListener('mousemove', this._boundHandleMove);
    document.addEventListener('touchmove', this._boundHandleMove, { passive: false });
    document.addEventListener('mouseup', this._boundStopInteraction);
    document.addEventListener('touchend', this._boundStopInteraction);

    // Button events
    cropBtn.addEventListener('click', () => this._cropImage());
    downloadBtn.addEventListener('click', () => this._downloadCroppedImage());
    resetBtn.addEventListener('click', () => this._resetCropBox());
    newImageBtn.addEventListener('click', () => this._resetToInitialState());
    useAsIsBtn.addEventListener('click', () => this._useImageAsIs());
    cropAnywayBtn.addEventListener('click', () => this._proceedToCrop());
  }

  /**
   *
   */
  _cleanup() {
    // Remove document event listeners
    if (this._boundHandleMove) {
      document.removeEventListener('mousemove', this._boundHandleMove);
      document.removeEventListener('touchmove', this._boundHandleMove);
    }
    if (this._boundStopInteraction) {
      document.removeEventListener('mouseup', this._boundStopInteraction);
      document.removeEventListener('touchend', this._boundStopInteraction);
    }
  }

  // ============================================================================
  // PRIVATE METHODS - File Handling
  // ============================================================================

  /**
   *
   * @param e
   */
  _handleFileSelect(e) {
    const file = e.target.files[0];
    if (file) {
      this._handleFileLoad(file);
    }
  }

  /**
   *
   * @param file
   */
  _handleFileLoad(file) {
    // Validate file
    const validation = this._validateFile(file);
    if (!validation.valid) {
      this._emitError(validation.error, validation.type);
      return;
    }

    this._originalBlob = file;
    const reader = new FileReader();

    reader.onload = (e) => {
      const img = new Image();
      img.onload = () => {
        this._image = img;
        this._analyzeAndDisplay();
      };
      img.onerror = () => {
        this._emitError('Failed to load image', 'load-error');
      };
      img.src = e.target.result;
    };

    reader.onerror = () => {
      this._emitError('Failed to read file', 'read-error');
    };

    reader.readAsDataURL(file);
  }

  /**
   *
   * @param file
   */
  _validateFile(file) {
    // Check file type
    if (!this._isValidFileType(file)) {
      return {
        valid: false,
        error: `Invalid file type. Accepted types: ${this._config.accept.join(', ')}`,
        type: 'file-type',
      };
    }

    // Check file size
    if (file.size > this._config.maxFileSize) {
      const maxMB = (this._config.maxFileSize / 1024 / 1024).toFixed(1);
      return {
        valid: false,
        error: `File too large. Maximum size: ${maxMB}MB`,
        type: 'file-size',
      };
    }

    return { valid: true };
  }

  /**
   *
   * @param file
   */
  _isValidFileType(file) {
    return this._config.accept.some((type) => {
      if (type.includes('/')) {
        // MIME type
        return file.type === type;
      } else {
        // Extension
        return file.name.toLowerCase().endsWith(type.toLowerCase());
      }
    });
  }

  // ============================================================================
  // PRIVATE METHODS - Image Analysis & Display
  // ============================================================================

  /**
   *
   */
  _analyzeAndDisplay() {
    const aspectData = this._calculateAspectRatio(this._image.width, this._image.height);
    this._isSquare = Math.abs(aspectData.ratio - 1) < 0.01;

    // Hide upload area
    this.shadowRoot.getElementById('uploadArea').style.display = 'none';

    // Emit image-loaded event
    this._emitEvent('image-loaded', {
      width: this._image.width,
      height: this._image.height,
      aspectRatio: aspectData.simplified,
      isTargetAspectRatio: this._isSquare,
    });

    // Show aspect ratio notice
    const notice = this.shadowRoot.getElementById('aspectRatioNotice');
    const ratioInfo = this.shadowRoot.getElementById('ratioInfo');
    const dimensionsInfo = this.shadowRoot.getElementById('dimensionsInfo');
    const squareActions = this.shadowRoot.getElementById('squareActions');

    notice.style.display = 'block';

    if (this._isSquare) {
      ratioInfo.innerHTML = `Aspect Ratio: 1:1 <span class="square-badge">Perfect Square!</span>`;
      dimensionsInfo.textContent = `Dimensions: ${aspectData.width} × ${aspectData.height}px`;
      squareActions.style.display = 'block';

      // Automatically show preview for square images
      setTimeout(() => {
        this._showSquarePreview();
      }, 500);
    } else {
      ratioInfo.textContent = `Aspect Ratio: ${aspectData.simplified} (${aspectData.ratio.toFixed(2)})`;
      dimensionsInfo.textContent = `Dimensions: ${aspectData.width} × ${aspectData.height}px | Needs cropping to 1:1`;
      squareActions.style.display = 'none';

      // Auto proceed to cropping after a short delay
      if (this._config.autoCrop) {
        setTimeout(() => {
          this._proceedToCrop();
        }, 1500);
      } else {
        setTimeout(() => {
          this._proceedToCrop();
        }, 1500);
      }
    }
  }

  /**
   *
   * @param width
   * @param height
   */
  _calculateAspectRatio(width, height) {
    const gcd = (a, b) => (b === 0 ? a : gcd(b, a % b));
    const divisor = gcd(width, height);
    return {
      ratio: width / height,
      simplified: `${width / divisor}:${height / divisor}`,
      width: width,
      height: height,
    };
  }

  /**
   *
   */
  _showSquarePreview() {
    const tempCanvas = document.createElement('canvas');
    const tempCtx = tempCanvas.getContext('2d');
    tempCanvas.width = this._image.width;
    tempCanvas.height = this._image.height;
    tempCtx.drawImage(this._image, 0, 0);

    const preview = this.shadowRoot.getElementById('preview');
    const previewContainer = this.shadowRoot.getElementById('previewContainer');
    const previewInfo = this.shadowRoot.getElementById('previewInfo');

    preview.src = tempCanvas.toDataURL();
    previewContainer.style.display = 'block';
    previewInfo.textContent = `Original square image: ${this._image.width} × ${this._image.height}px`;
  }

  /**
   *
   */
  _useImageAsIs() {
    const tempCanvas = document.createElement('canvas');
    const tempCtx = tempCanvas.getContext('2d');
    tempCanvas.width = this._image.width;
    tempCanvas.height = this._image.height;
    tempCtx.drawImage(this._image, 0, 0);

    tempCanvas.toBlob((blob) => {
      this._croppedBlob = blob;

      // Hide aspect ratio notice
      this.shadowRoot.getElementById('aspectRatioNotice').style.display = 'none';

      if (this._config.showPreview) {
        // Show download button only if preview is enabled
        const downloadBtn = this.shadowRoot.getElementById('downloadBtn');
        downloadBtn.style.display = 'inline-flex';
        downloadBtn.classList.remove('hidden');
        this.shadowRoot.getElementById('controls').style.display = 'flex';

        // Show preview
        const preview = this.shadowRoot.getElementById('preview');
        const previewContainer = this.shadowRoot.getElementById('previewContainer');
        const previewInfo = this.shadowRoot.getElementById('previewInfo');

        preview.src = tempCanvas.toDataURL();
        previewContainer.style.display = 'block';
        previewInfo.textContent = `Ready: ${this._image.width} × ${this._image.height}px (1:1)`;
      } else {
        // If no preview, hide everything after a brief moment
        setTimeout(() => {
          this.shadowRoot.getElementById('aspectRatioNotice').style.display = 'none';
          this.shadowRoot.getElementById('uploadArea').style.display = 'block';
        }, 300);
      }

      // Emit crop-complete event
      this._emitEvent('crop-complete', {
        blob: blob,
        dataUrl: tempCanvas.toDataURL(),
        dimensions: {
          width: this._image.width,
          height: this._image.height,
        },
      });
    }, this._config.outputFormat);
  }

  /**
   *
   */
  _proceedToCrop() {
    // Hide aspect ratio notice
    this.shadowRoot.getElementById('aspectRatioNotice').style.display = 'none';
    this.shadowRoot.getElementById('previewContainer').style.display = 'none';

    // Continue with normal cropping flow
    this._displayImage();
  }

  /**
   *
   */
  _displayImage() {
    const canvas = this.shadowRoot.getElementById('canvas');
    const ctx = canvas.getContext('2d');

    // Get actual available space in the container
    // This works correctly whether in a dialog or full page
    const container = this.shadowRoot.querySelector('.container');
    const containerRect = container.getBoundingClientRect();

    // Use container's actual width and reasonable height limits
    // Leave some padding for controls (200px) and margins (40px)
    const maxWidth = Math.min(800, containerRect.width - 40);
    const maxHeight = Math.min(600, containerRect.width - 40); // Use width-based limit for dialogs

    let width = this._image.width;
    let height = this._image.height;

    if (width > maxWidth || height > maxHeight) {
      const ratio = Math.min(maxWidth / width, maxHeight / height);
      width *= ratio;
      height *= ratio;
      this._scale = ratio;
    } else {
      this._scale = 1;
    }

    canvas.width = width;
    canvas.height = height;
    ctx.drawImage(this._image, 0, 0, width, height);

    // Show cropper UI
    this.shadowRoot.getElementById('cropperContainer').style.display = 'block';
    this.shadowRoot.getElementById('controls').style.display = 'flex';

    // Hide download button during cropping phase (only show after crop complete)
    const downloadBtn = this.shadowRoot.getElementById('downloadBtn');
    downloadBtn.style.display = 'none';
    downloadBtn.classList.add('hidden');
    this.shadowRoot.getElementById('previewContainer').style.display = 'none';

    // Initialize crop box after a brief moment to ensure canvas is rendered
    requestAnimationFrame(() => {
      this._initCropBox();
    });
  }

  // ============================================================================
  // PRIVATE METHODS - Crop Box Management
  // ============================================================================

  /**
   *
   */
  _initCropBox() {
    const canvas = this.shadowRoot.getElementById('canvas');

    // Calculate ideal crop box size
    // For very rectangular images, use a more generous percentage
    const aspectRatio = canvas.width / canvas.height;
    let percentage;

    if (aspectRatio > 2 || aspectRatio < 0.5) {
      // Very wide or very tall - use 85% of smaller dimension
      percentage = 0.85;
    } else {
      // More square-ish - use 75% of smaller dimension
      percentage = 0.75;
    }

    const size = Math.min(canvas.width, canvas.height) * percentage;

    // Center the crop box
    this._cropData = {
      x: (canvas.width - size) / 2,
      y: (canvas.height - size) / 2,
      size: size,
    };

    this._updateCropBox();
  }

  /**
   *
   */
  _updateCropBox() {
    const cropBox = this.shadowRoot.getElementById('cropBox');
    cropBox.style.left = `${this._cropData.x}px`;
    cropBox.style.top = `${this._cropData.y}px`;
    cropBox.style.width = `${this._cropData.size}px`;
    cropBox.style.height = `${this._cropData.size}px`;

    // Emit crop-change event
    this._emitEvent('crop-change', {
      x: this._cropData.x / this._scale,
      y: this._cropData.y / this._scale,
      width: this._cropData.size / this._scale,
      height: this._cropData.size / this._scale,
    });
  }

  /**
   *
   * @param e
   */
  _startDrag(e) {
    if (e.target.classList.contains('crop-handle')) return;

    e.preventDefault();
    this._isDragging = true;

    const canvas = this.shadowRoot.getElementById('canvas');
    const rect = canvas.getBoundingClientRect();
    const touch = e.touches ? e.touches[0] : e;
    this._startX = touch.clientX - rect.left - this._cropData.x;
    this._startY = touch.clientY - rect.top - this._cropData.y;
  }

  /**
   *
   * @param e
   */
  _startResize(e) {
    e.preventDefault();
    e.stopPropagation();
    this._isResizing = true;
    this._currentHandle = e.target.dataset.handle;

    const canvas = this.shadowRoot.getElementById('canvas');
    const rect = canvas.getBoundingClientRect();
    const touch = e.touches ? e.touches[0] : e;
    this._startX = touch.clientX - rect.left;
    this._startY = touch.clientY - rect.top;
  }

  /**
   *
   * @param e
   */
  _handleMove(e) {
    if (!this._isDragging && !this._isResizing) return;

    e.preventDefault();
    const canvas = this.shadowRoot.getElementById('canvas');
    const rect = canvas.getBoundingClientRect();
    const touch = e.touches ? e.touches[0] : e;
    const currentX = touch.clientX - rect.left;
    const currentY = touch.clientY - rect.top;

    if (this._isDragging) {
      this._cropData.x = Math.max(0, Math.min(currentX - this._startX, canvas.width - this._cropData.size));
      this._cropData.y = Math.max(0, Math.min(currentY - this._startY, canvas.height - this._cropData.size));
      this._updateCropBox();
    } else if (this._isResizing) {
      this._handleResize(currentX, currentY, canvas);
    }
  }

  /**
   *
   * @param currentX
   * @param currentY
   * @param canvas
   */
  _handleResize(currentX, currentY, canvas) {
    const minSize = 50;
    let newSize = this._cropData.size;
    let newX = this._cropData.x;
    let newY = this._cropData.y;

    switch (this._currentHandle) {
      case 'se':
        newSize = Math.max(
          minSize,
          Math.min(
            currentX - this._cropData.x,
            currentY - this._cropData.y,
            canvas.width - this._cropData.x,
            canvas.height - this._cropData.y
          )
        );
        break;
      case 'sw': {
        const swSize = Math.max(
          minSize,
          Math.min(
            this._cropData.x + this._cropData.size - currentX,
            currentY - this._cropData.y,
            this._cropData.x + this._cropData.size,
            canvas.height - this._cropData.y
          )
        );
        newX = this._cropData.x + this._cropData.size - swSize;
        newSize = swSize;
        break;
      }
      case 'ne': {
        const neSize = Math.max(
          minSize,
          Math.min(
            currentX - this._cropData.x,
            this._cropData.y + this._cropData.size - currentY,
            canvas.width - this._cropData.x,
            this._cropData.y + this._cropData.size
          )
        );
        newY = this._cropData.y + this._cropData.size - neSize;
        newSize = neSize;
        break;
      }
      case 'nw': {
        const nwSize = Math.max(
          minSize,
          Math.min(
            this._cropData.x + this._cropData.size - currentX,
            this._cropData.y + this._cropData.size - currentY,
            this._cropData.x + this._cropData.size,
            this._cropData.y + this._cropData.size
          )
        );
        newX = this._cropData.x + this._cropData.size - nwSize;
        newY = this._cropData.y + this._cropData.size - nwSize;
        newSize = nwSize;
        break;
      }
    }

    this._cropData.x = newX;
    this._cropData.y = newY;
    this._cropData.size = newSize;
    this._updateCropBox();
  }

  /**
   *
   */
  _stopInteraction() {
    this._isDragging = false;
    this._isResizing = false;
    this._currentHandle = null;
  }

  // ============================================================================
  // PRIVATE METHODS - Cropping
  // ============================================================================

  /**
   *
   */
  _cropImage() {
    const cropCanvas = document.createElement('canvas');
    const cropCtx = cropCanvas.getContext('2d');

    // Calculate actual crop dimensions on original image
    const actualX = this._cropData.x / this._scale;
    const actualY = this._cropData.y / this._scale;
    const actualSize = this._cropData.size / this._scale;

    cropCanvas.width = actualSize;
    cropCanvas.height = actualSize;

    cropCtx.drawImage(this._image, actualX, actualY, actualSize, actualSize, 0, 0, actualSize, actualSize);

    // Disable crop button while processing
    const cropBtn = this.shadowRoot.getElementById('cropBtn');
    const originalText = cropBtn.innerHTML;
    cropBtn.disabled = true;
    cropBtn.innerHTML = '⏳ Processing...';

    // Convert to blob
    cropCanvas.toBlob((blob) => {
      this._croppedBlob = blob;

      // Re-enable button
      cropBtn.disabled = false;
      cropBtn.innerHTML = originalText;

      // Show preview if enabled
      if (this._config.showPreview) {
        this._showPreview(cropCanvas.toDataURL(), actualSize);
      } else {
        // If no preview, hide the cropper UI after a brief moment
        setTimeout(() => {
          this.shadowRoot.getElementById('cropperContainer').style.display = 'none';
          this.shadowRoot.getElementById('controls').style.display = 'none';
          this.shadowRoot.getElementById('uploadArea').style.display = 'block';
        }, 300);
      }

      // Emit crop-complete event
      this._emitEvent('crop-complete', {
        blob: blob,
        dataUrl: cropCanvas.toDataURL(),
        dimensions: {
          width: actualSize,
          height: actualSize,
        },
      });
    }, this._config.outputFormat);
  }

  /**
   *
   * @param dataUrl
   * @param size
   */
  _showPreview(dataUrl, size) {
    if (!this._config.showPreview) return;

    const preview = this.shadowRoot.getElementById('preview');
    const previewContainer = this.shadowRoot.getElementById('previewContainer');
    const downloadBtn = this.shadowRoot.getElementById('downloadBtn');
    const previewInfo = this.shadowRoot.getElementById('previewInfo');

    preview.src = dataUrl;
    previewContainer.style.display = 'block';
    downloadBtn.style.display = 'inline-flex';
    downloadBtn.classList.remove('hidden'); // Remove hidden class to show button

    previewInfo.textContent = `Size: ${Math.round(size)} × ${Math.round(size)}px (1:1)`;
  }

  /**
   *
   */
  _downloadCroppedImage() {
    if (!this._croppedBlob) return;

    const url = URL.createObjectURL(this._croppedBlob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `cropped-image-${Date.now()}.png`;
    link.click();
    URL.revokeObjectURL(url);
  }

  /**
   *
   */
  _resetCropBox() {
    this._initCropBox();
    this.shadowRoot.getElementById('previewContainer').style.display = 'none';
    const downloadBtn = this.shadowRoot.getElementById('downloadBtn');
    downloadBtn.style.display = 'none';
    downloadBtn.classList.add('hidden');
  }

  /**
   *
   */
  _resetToInitialState() {
    this.shadowRoot.getElementById('uploadArea').style.display = 'block';
    this.shadowRoot.getElementById('cropperContainer').style.display = 'none';
    this.shadowRoot.getElementById('controls').style.display = 'none';
    this.shadowRoot.getElementById('previewContainer').style.display = 'none';
    this.shadowRoot.getElementById('aspectRatioNotice').style.display = 'none';
    this.shadowRoot.getElementById('fileInput').value = '';

    // Ensure download button is hidden
    const downloadBtn = this.shadowRoot.getElementById('downloadBtn');
    downloadBtn.classList.add('hidden');

    this._image = null;
    this._originalBlob = null;
    this._croppedBlob = null;
    this._isSquare = false;
  }

  // ============================================================================
  // PRIVATE METHODS - Utilities
  // ============================================================================

  /**
   *
   * @param value
   * @param configKey
   */
  _parseDimensions(value, configKey) {
    if (!value) return;
    const parts = value.toLowerCase().split('x');
    if (parts.length === 2) {
      this._config[configKey] = {
        width: parseInt(parts[0]) || 64,
        height: parseInt(parts[1]) || 64,
      };
    }
  }

  /**
   *
   * @param name
   * @param detail
   */
  _emitEvent(name, detail) {
    this.dispatchEvent(
      new CustomEvent(name, {
        detail,
        bubbles: true,
        composed: true,
      })
    );
  }

  /**
   *
   * @param message
   * @param type
   */
  _emitError(message, type) {
    this._emitEvent('error', { message, type });
    console.error(`[ImageCropper] ${type}:`, message);
  }
}

